/**
 * Soru bankasının metin işleri.
 *
 * Sayfadan ayrı duruyor çünkü buradaki kuralların uygulamadaki
 * `TextNormalize` / `AnswerGroup` ile birebir aynı davranması gerekiyor;
 * bileşenlerin içine dağılınca ikisinin ayrışmaması imkânsız oluyor.
 */

/** lib/models/quiz_question.dart -> QuizQuestion.minAnswers ile aynı olmalı. */
export const MIN_ANSWERS = 12;

/**
 * Türkçe duyarlı küçük harf.
 *
 * Sıra önemli: JS'in toLowerCase()'i 'I' → 'i' yapıyor, oysa Türkçede 'ı'.
 * Önce değiştirmezsek "Irmak" → "irmak" olup kayıtlı "ırmak" ile
 * eşleşmiyor. lib/services/text_normalize.dart ile aynı adımlar.
 */
export function normalize(s: string): string {
  return s
    .trim()
    .replace(/I/g, "ı")
    .replace(/İ/g, "i")
    .toLowerCase()
    .replace(/̇/g, "");
}

/** "ABD | amerika" → "abd|amerika"; ilk yazım kanonik. */
export function normalizeAnswerLine(raw: string): string {
  return raw
    .split("|")
    .map(normalize)
    .filter(Boolean)
    .join("|");
}

export function canonicalOf(line: string): string {
  return line.split("|")[0] ?? "";
}

export function formsOf(line: string): string[] {
  return line.split("|").filter(Boolean);
}

/** Bir cevap listesindeki tüm yazımlar — mükerrer kontrolü bunun üzerinden. */
export function allForms(answers: string[]): Set<string> {
  const set = new Set<string>();
  answers.forEach((line) => formsOf(line).forEach((f) => set.add(f)));
  return set;
}

/**
 * Bir bloğun bankaya nasıl yansıyacağı.
 *
 * `merge`, paketi ikinci kez aktarmayı işe yarar hale getiriyor: soru
 * metinleri sabit kaldığı için havuz büyüdükçe gelen blok bankadakiyle
 * örtüşüyor, ama cevap listesi daha uzun oluyor.
 */
export type ImportMode = "create" | "merge" | "skip";

export type ParsedQuestion = {
  text: string;
  answers: string[];
  mode: ImportMode;
  /** merge modunda güncellenecek sorunun kimliği. */
  mergeId?: string;
  /** merge modunda gerçekten yazılacak satırlar — bankada olmayanlar. */
  newAnswers: string[];
  /** Engelleyici sorunlar — bunlar varsa soru içe aktarılmaz. */
  errors: string[];
  /** Aktarmayı engellemeyen notlar. */
  warnings: string[];
};

/** Bankada duran bir soru; metni değil, güncellenecek yanları. */
export type ExistingQuestion = { id: string; answers: string[] };

type ParseOptions = {
  /** Bankada halihazırda duran sorular, normalize metne göre. */
  existing: Map<string, ExistingQuestion>;
};

/**
 * Yapıştırılan metni sorulara ayırır.
 *
 * İki biçim kabul ediliyor:
 *  1. Blok: ilk satır soru, sonraki satırlar cevaplar, bloklar boş satırla
 *     ayrılır. Eşanlamlılar aynı satırda `|` ile.
 *  2. JSON: `[{ "text": "...", "answers": ["...", ...] }]` ya da
 *     `{ "questions": [ ... ] }` — hazır paketle aynı şekil.
 *
 * Bankada aynı metinli soru varsa blok atlanmaz, o sorunun üstüne eklenir:
 * kaydı silip yeniden yazmak etiketleri, yayın durumunu ve o soruya bağlı
 * oyuncu önerilerini götürürdü.
 *
 * Hiçbir şey yazmaz; yalnızca ne olacağını anlatır. Aktarma öncesi
 * önizleme bunun çıktısını gösteriyor.
 */
export function parseImport(
  raw: string,
  opts: ParseOptions
): ParsedQuestion[] {
  const trimmed = raw.trim();
  if (!trimmed) return [];

  const parsed =
    trimmed.startsWith("[") || trimmed.startsWith("{")
      ? parseJson(trimmed)
      : parseBlocks(trimmed);

  // Yapıştırmanın kendi içindeki tekrarlar: ilki kalır, sonrakiler işaretlenir.
  const seen = new Set<string>();
  for (const q of parsed) {
    const key = normalize(q.text);
    if (!q.text.trim()) {
      q.errors.push("Soru metni boş");
    } else if (seen.has(key)) {
      q.errors.push("Yapıştırmada iki kez geçiyor");
    } else {
      seen.add(key);
      const match = opts.existing.get(key);
      if (match) {
        // Mükerrer kontrolü tüm yazımlara karşı; eşanlamlı da çakışmamalı.
        const known = allForms(match.answers);
        q.mergeId = match.id;
        q.newAnswers = q.answers.filter((line) =>
          formsOf(line).every((f) => !known.has(f))
        );
        if (q.newAnswers.length === 0) {
          q.mode = "skip";
          q.warnings.push("Bankadaki soru zaten güncel");
        } else {
          q.mode = "merge";
        }
      }
    }

    if (q.answers.length === 0) {
      q.errors.push("Hiç cevap yok");
    } else if (q.mode === "create" && q.answers.length < MIN_ANSWERS) {
      q.warnings.push(
        `${MIN_ANSWERS - q.answers.length} cevap eksik — taslak olarak eklenir`
      );
    }
  }
  return parsed;
}

function dedupeAnswers(lines: string[]): { answers: string[]; dropped: number } {
  const known = new Set<string>();
  const answers: string[] = [];
  let dropped = 0;
  for (const line of lines) {
    const norm = normalizeAnswerLine(line);
    if (!norm) continue;
    const forms = formsOf(norm);
    if (forms.some((f) => known.has(f))) {
      dropped++;
      continue;
    }
    forms.forEach((f) => known.add(f));
    answers.push(norm);
  }
  return { answers, dropped };
}

function parseBlocks(raw: string): ParsedQuestion[] {
  return raw
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean)
    .map((block) => {
      const lines = block
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
      const { answers, dropped } = dedupeAnswers(lines.slice(1));
      return {
        text: lines[0] ?? "",
        answers,
        mode: "create",
        newAnswers: [],
        errors: [],
        warnings: dropped
          ? [`${dropped} tekrar eden cevap atıldı`]
          : [],
      };
    });
}

function parseJson(raw: string): ParsedQuestion[] {
  try {
    const data = JSON.parse(raw);
    const list: unknown[] = Array.isArray(data)
      ? data
      : Array.isArray((data as { questions?: unknown[] }).questions)
      ? (data as { questions: unknown[] }).questions
      : [];
    return list.map((item) => {
      const o = (item ?? {}) as { text?: unknown; answers?: unknown };
      const lines = Array.isArray(o.answers)
        ? o.answers.map((a) => String(a))
        : [];
      const { answers, dropped } = dedupeAnswers(lines);
      return {
        text: typeof o.text === "string" ? o.text.trim() : "",
        answers,
        mode: "create" as const,
        newAnswers: [],
        errors: [],
        warnings: dropped ? [`${dropped} tekrar eden cevap atıldı`] : [],
      };
    });
  } catch (e) {
    return [
      {
        text: "",
        answers: [],
        mode: "create",
        newAnswers: [],
        errors: ["JSON okunamadı: " + (e as Error).message],
        warnings: [],
      },
    ];
  }
}

export function formatDate(ms?: number): string {
  if (!ms) return "—";
  return new Date(ms).toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
}

export function relativeTime(ms?: number): string {
  if (!ms) return "—";
  const diff = Date.now() - ms;
  const m = Math.round(diff / 60000);
  if (m < 1) return "az önce";
  if (m < 60) return `${m} dk önce`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} sa önce`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} gün önce`;
  return formatDate(ms);
}
